import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink, // Alias to avoid lint warnings
  Typography,
} from '@saviynt/design-system';

import { FormLayout, WindowContext } from '../../../shared/components';
import { urls } from '../../../shared/utils';
import { getMfaStatus, login } from '../data-sources';

import './LoginForm.css';

interface Props {
  welcomeMessage: string;
  versionMessage: string;
}

/**
 * Login form component with username and password fields.
 */
function LoginForm({ welcomeMessage, versionMessage }: Props): JSX.Element {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [hasLoginError, setHasLoginError] = useState(false);
  const { locationAssign } = useContext(WindowContext);

  const history = useHistory();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    handleSubmitAsync().catch(() => {
      setHasLoginError(true);
      setIsLoggingIn(false);
    });

    async function handleSubmitAsync() {
      setIsLoggingIn(true);
      setHasLoginError(false);
      const response = await login({ username, password });

      if (response.isPasswordExpired) {
        const data = {
          oneTimeToken: response.oneTimeToken,
          passwordValidationConfig: response.passwordValidationConfig,
        };

        const encoded = btoa(JSON.stringify(data));

        history.push(`${urls['Update Password']}?data=${encoded}`);

        return;
      }

      const mfaStatus = await getMfaStatus();

      if (mfaStatus.requiresRegistration) {
        history.push(urls['MFA Setup']);

        return;
      }

      if (mfaStatus.enabled && !mfaStatus.loginVerified) {
        history.push(urls['MFA Verification']);

        return;
      }

      locationAssign(urls.LoginInit);
    }
  };

  const canContinue = username && password;
  const passwordType = showPassword ? 'text' : 'password';

  const footer = (
    <Button
      type='submit'
      kind='filled'
      isDisabled={!canContinue}
      isLoading={isLoggingIn}
      loaderText='Logging in'
      className='Auth_LoginForm-continueButton'>
      Continue
    </Button>
  );

  const error = hasLoginError && (
    <InlineMessage
      kind='background'
      colorTheme='critical'
      leftIcon={<Icon kind='AlertCriticalFilled' />}>
      <>
        <Typography kind='label'>Login failed</Typography>
        <Typography kind='body2'>
          Please make sure your username and password are correct. Too many
          attempts might lock you out.
        </Typography>
      </>
    </InlineMessage>
  );

  return (
    <FormLayout
      title='Enter your credentials'
      subtitle={welcomeMessage}
      versionMessage={versionMessage}
      footerContent={footer}
      errorContent={error}
      onSubmit={handleSubmit}>
      <InputField
        name='username'
        placeholder='Enter Your Username'
        value={username}
        setValue={setUsername}
        isResetButtonVisible={false}
      />
      <InputField
        name='password'
        placeholder='Password'
        type={passwordType}
        value={password}
        suffixButtonType='password'
        suffixOnClick={() => setShowPassword(!showPassword)}
        setValue={setPassword}
        isResetButtonVisible={false}
      />
      <DSLink
        kind='button'
        text='Forgot password?'
        className='Auth_LoginForm-forgotPasswordLink'
        onClick={() => locationAssign(urls.ForgotPassword)}
      />
    </FormLayout>
  );
}

export default LoginForm;
